import { useState } from 'react';
import dayjs from 'dayjs';
import GrafanaDatePicker from './components/GrafanaDatePicker';
import type { DatePickerProps } from './types';

function App() {
  const [dateRange, setDateRange] = useState<{ from: dayjs.Dayjs; to: dayjs.Dayjs } | null>(null);
  const [customRange, setCustomRange] = useState<{ from: dayjs.Dayjs; to: dayjs.Dayjs }>({
    from: dayjs().startOf('month'),
    to: dayjs().endOf('month'),
  });

  const customQuickOptions: DatePickerProps['quickOptions'] = [
    { label: 'Last 5 min', from: () => dayjs().subtract(5, 'minute'), to: () => dayjs(), display: 'Now-5m to Now' },
    { label: 'Last 30 min', from: () => dayjs().subtract(30, 'minute'), to: () => dayjs(), display: 'Now-30m to Now' },
    { label: 'Last 1 hour', from: () => dayjs().subtract(1, 'hour'), to: () => dayjs(), display: 'Now-1h to Now' },
    { label: 'Last 6 hours', from: () => dayjs().subtract(6, 'hour'), to: () => dayjs(), display: 'Now-6h to Now' },
    { label: 'Last 24 hours', from: () => dayjs().subtract(24, 'hour'), to: () => dayjs(), display: 'Now-24h to Now' },
    { label: 'Last 7 days', from: () => dayjs().subtract(6, 'day').startOf('day'), to: () => dayjs().endOf('day'), display: 'Last 7 days' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Grafana 风格日期选择器演示
        </h1>

        <div className="space-y-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">基础用法</h2>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              选择日期范围
            </label>
            <GrafanaDatePicker
              value={dateRange || undefined}
              onChange={setDateRange}
            />

            {dateRange && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">选中的日期范围：</h3>
                <div className="font-mono text-sm">
                  <div>开始: {dateRange.from.format('YYYY-MM-DD HH:mm:ss')}</div>
                  <div>结束: {dateRange.to.format('YYYY-MM-DD HH:mm:ss')}</div>
                  <div>时长: {dateRange.to.diff(dateRange.from, 'hour')} 小时</div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">自定义快捷选项</h2>
            <p className="text-sm text-gray-500 mb-4">使用 <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">quickOptions</code> 和 <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">quickOptionGroups</code> 自定义快捷范围</p>
            <GrafanaDatePicker
              value={customRange}
              onChange={setCustomRange}
              quickOptions={customQuickOptions}
              quickOptionGroups={[{ label: 'Recent', indices: [0, 6] }]}
            />

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="font-mono text-sm">
                <div>开始: {customRange.from.format('YYYY-MM-DD HH:mm:ss')}</div>
                <div>结束: {customRange.to.format('YYYY-MM-DD HH:mm:ss')}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">隐藏时区选择</h2>
            <GrafanaDatePicker
              showTimezone={false}
            />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">功能特点</h2>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• 快速选择预设时间范围（最近5分钟到最近90天）</li>
              <li>• 自定义日期时间选择器</li>
              <li>• 支持表达式输入（如: now-1h to now）</li>
              <li>• 时区选择功能</li>
              <li>• 日历视图快速选择日期范围</li>
              <li>• 支持自定义快捷选项和分组</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
